/**
 * Token Refresh Helper
 *
 * The access token lives in memory (tokenStore) and the refresh token in
 * an httpOnly cookie. Refresh calls go to the server with
 * `credentials: 'include'` — the browser attaches the cookie; the server
 * hands back a fresh access token in the JSON body which we put into the
 * in-memory tokenStore.
 *
 * Phase 5 (complete): No localStorage mirrors. The token surface is
 * memory (access) + httpOnly cookie (refresh) only.
 */
import tokenStore from './tokenStore';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

let refreshInFlight = null;

export function getAccessToken() {
  return tokenStore.getAccessToken();
}

export function storeTokens({ access_token } = {}) {
  if (access_token) {
    tokenStore.setAccessToken(access_token);
  }
}

export function clearTokens() {
  tokenStore.clearAccessToken();
}

/**
 * Exchange the refresh token (httpOnly cookie) for a fresh access token.
 * Concurrent callers share a single in-flight promise.
 *
 * Returns the new access token on success, or null when refresh failed.
 */
export async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const r = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // sends maidly_refresh httpOnly cookie
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!r.ok) {
        clearTokens();
        return null;
      }
      const data = await r.json();
      if (data && data.access_token) {
        storeTokens({ access_token: data.access_token });
        return data.access_token;
      }
      return null;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[TokenRefresh] network error', e);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * fetchWithAuth — drop-in fetch() that adds the Authorization header and
 * transparently retries once on 401 after refreshing the access token.
 */
export async function fetchWithAuth(input, init = {}) {
  const headers = new Headers(init.headers || {});
  const access = getAccessToken();
  if (access) headers.set('Authorization', `Bearer ${access}`);
  // Always send cookies so the server can authenticate via cookie too if needed.
  let res = await fetch(input, { ...init, headers, credentials: 'include' });
  if (res.status !== 401) return res;

  const newAccess = await refreshAccessToken();
  if (!newAccess) return res;

  headers.set('Authorization', `Bearer ${newAccess}`);
  res = await fetch(input, { ...init, headers, credentials: 'include' });
  return res;
}
